/**
 * Entry point and router (docs/spec/06 §6). Everything is reachable by URL so an agent can drive it:
 *
 *   /                                  the game
 *   /?play=classic&cave=A&level=1      straight into a cave (play=classic|deep|expedition|daily|demo)
 *   /?cave=SF1.<code>                  a shared cave, in Practice
 *   /?screen=<name>                    any menu screen
 *   /?demo=1                           the attract demo
 *   /?scene=sprites                    every sprite in several palettes
 *   /?scene=caves&set=classic&level=1  every cave of a set as thumbnails
 *   /?scene=compare&set=classic&cave=A one live cave in all four looks side by side
 *   /?fx=authentic|enhanced|remastered presentation preset for this load    &fresh=1  no saved data, no world tables
 */

import { createAudio } from '../audio';
import { createAutopilot } from '../bot';
import { Controls } from '../input/controls';
import { keyName } from '../input/keyNames';
import { isTouchDevice, TouchControls } from '../input/touch';
import { CaveLibrary } from '../platform/caveLibrary';
import { OptionsStore, withPreset } from '../platform/options';
import { Records } from '../platform/records';
import { ScoreBook } from '../platform/scoreBook';
import { ScoreSync } from '../platform/scoreSync';
import { browserStorage, memoryStorage } from '../platform/storage';
import { fetchWorldTables, postWorldScore } from '../platform/worldScores';
import { Display } from '../render/display/display';
import { Screen } from '../render/screen';
import type { Scene, Services } from '../ui/scene';
import { VERSION } from './brand';
import { installDebugApi } from './debugApi';
import { installFavicon } from './favicon';
import { createFactory } from './flow';
import { GameScene } from './game/gameScene';
import { App } from './host';
import { cavesScene, spritesScene } from './lookdev';
import { compareScene } from './lookdevCompare';
import { num, pick, query } from './params';
import { caveSet, findCave } from '../caves';
import { bootRoute } from './routes';

function boot(): void {
  installFavicon();
  const canvas = document.getElementById('screen') as HTMLCanvasElement;
  const live = document.getElementById('live');
  const storage = query.has('fresh') ? memoryStorage() : browserStorage;
  const options = new OptionsStore(storage);
  const fx = query.get('fx');
  if (fx === 'authentic' || fx === 'enhanced' || fx === 'remastered') options.override(withPreset(options.get(), fx));

  const audio = createAudio();
  const applyVolumes = (): void => {
    const o = options.get();
    audio.setVolumes({ master: o.master / 10, sfx: o.sfx / 10, music: o.music / 10 });
  };
  applyVolumes();
  options.subscribe(applyVolumes);

  const controls = new Controls(options);
  const screen = new Screen(320, 200);
  const display = new Display(canvas);
  let touchWanted = false;
  let touch: TouchControls | null = null;
  const showTouch = (): void => {
    const mode = options.get().touch;
    const on = touchWanted && (mode === 'on' || (mode === 'auto' && isTouchDevice()));
    touch?.setVisible(on);
  };
  options.subscribe(showTouch);

  const scores = new ScoreBook(storage);
  // With ?fresh=1 (tests, a clean look) the world tables stay out of it too.
  const sync = query.has('fresh') ? null : new ScoreSync(scores, { fetchTables: fetchWorldTables, post: postWorldScore });
  sync?.start();
  window.addEventListener('online', () => void sync?.flush());

  const services: Services = {
    audio,
    options,
    storage,
    scores,
    world: { refresh: () => void sync?.refresh() },
    records: new Records(storage),
    library: new CaveLibrary(storage),
    input: { keyName, gamepad: () => controls.gamepad, touch: () => touch?.visible === true },
    announce(text) {
      if (live) live.textContent = text;
    },
    today() {
      // ?date=2026-09-23 pins the day, so tests and screenshots do not change at midnight.
      const pinned = /^(\d{4})-(\d{2})-(\d{2})$/.exec(query.get('date') ?? '');
      if (pinned) return { year: Number(pinned[1]), month: Number(pinned[2]), day: Number(pinned[3]) };
      const d = new Date();
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
    },
    version: VERSION,
  };

  const factory = createFactory({
    audio,
    latch: controls.latch,
    pilot() {
      const bot = createAutopilot({ seed: Date.now() & 0xffff });
      return (s) => bot.next(s);
    },
    touch(visible) {
      touchWanted = visible;
      showTouch();
    },
  });
  const app = new App(services, factory, screen, display, controls);
  touch = new TouchControls(controls.latch, (a) => app.top?.action?.(a), () => audio.unlock());

  controls.attach({
    key: (k) => app.top?.keydown?.(k) ?? false,
    action: (a) => app.top?.action?.(a),
    any: () => audio.unlock(),
  });
  const pointer = (kind: 'move' | 'down' | 'up', e: PointerEvent): void => {
    if (kind === 'down') audio.unlock();
    const v = display.toVirtual(e.clientX, e.clientY);
    if (v) app.pointer({ x: v.x, y: v.y, kind, button: e.button, delta: 0 });
  };
  canvas.addEventListener('pointerdown', (e) => pointer('down', e));
  canvas.addEventListener('pointermove', (e) => pointer('move', e));
  canvas.addEventListener('pointerup', (e) => pointer('up', e));
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('wheel', (e) => {
    const v = display.toVirtual(e.clientX, e.clientY);
    if (v) app.pointer({ x: v.x, y: v.y, kind: 'wheel', button: 0, delta: Math.sign(e.deltaY) });
  }, { passive: true });

  const scene = query.get('scene');
  const lookSet = pick('set', ['classic', 'deep'] as const, 'classic');
  const compareDef = findCave(caveSet(lookSet), (query.get('cave') ?? 'A').toUpperCase());
  if (scene === 'sprites' || scene === 'caves' || (scene === 'compare' && compareDef)) {
    const look: Scene = scene === 'sprites' ? spritesScene(pick('art', ['c64', 'remastered'] as const, 'c64'))
      : scene === 'caves' ? cavesScene(lookSet, num('level', 1))
      : compareDef ? compareScene(compareDef, num('level', 1)) : spritesScene();
    const lookApp = new App(services, () => look, screen, display, controls);
    lookApp.frozen = query.has('freeze');
    lookApp.go({ to: 'title' });
    installDebugApi(lookApp, controls);
    lookApp.run();
    return;
  }
  const autoPause = (): void => {
    const top = app.top;
    if (top instanceof GameScene) top.autoPause();
  };
  window.addEventListener('blur', autoPause);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) autoPause();
  });
  app.frozen = query.has('freeze');
  app.go(bootRoute());
  installDebugApi(app, controls);
  (window as unknown as { __music?: () => string[] }).__music = () => audio.tracks();
  app.run();
}

boot();
